/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter';
import { BaseFeatureMock } from '../mockServer/featureMock';
import {
    storeDetailsFactory,
    storeListItemDtoFactory,
    storeCashBoxesFactory,
    storeCashBoxesStatsFactory,
    turnoversByCashBoxFactory,
    storeDetailsForMapFactory,
    storeOnMapFactory,
    coordinatesHistoryItemFactory,
    inspectionsFactory,
    storePhotoInfoFactory,
} from './storesFactories';
import {
    StoreDetailsDto,
    StoreCashboxesListDto,
    StoreDetailsForMap,
    UpdatedStoreDetailsDto,
    StoreCoordinatesHistoryDto,
    StoreInspectionsHistoryDto,
    StoreInspectionDto,
    InspectionStatus,
    StoresListDto,
    StoresForMap,
    StorePoint,
    StorePhotoInfoDto,
} from './storesClientTypings';
import { RegistryTurnoversByPeriodDto, RegistryTurnoversByCashBoxesDto } from '../apiCommon.typings';
import { TurnoversByPeriodFactory } from '../cashBoxes/cashBoxesFactories';
import moment from 'moment';
import { CashBoxesStats } from '../cashBoxes/cashBoxesClient.typings';

const STORES_URL: RegExp = /\/stores$/;
const STORES_FOR_MAP_URL: RegExp = /\/map\/stores/;
const STORES_DETAILS_FOR_MAP_URL: RegExp = /map\/stores\/\d+$/;
const STORES_DETAILS_URL: RegExp = /\/stores\/(\d+)$/;
const STORES_CASHBOXES_URL: RegExp = /\/stores\/(\d+)\/cashboxes$/;
const STORE_CASHBOXES_STATS: RegExp = /\/stores\/(\d+)\/cashboxes\/stats/;
const COORDINATES_HISTORY: RegExp = /\/stores\/(\d+)\/coordinatesHistory/;
const INSPECTIONS: RegExp = /\/stores\/(\d+)\/inspections/;
const STORES_GET_TURNOVERS_BY_PERIOD: RegExp = /\/stores\/\d+\/turnovers/;
const STORES_GET_TURNOVERS_BY_CASHBOXES: RegExp = /\/stores\/\d+\/cashboxes\/turnovers/;
const STORE_PHOTOS_URL: RegExp = /\/stores\/(\d+)\/photos\/(photos-stores|photos-inspections)$/;
const STORE_UPLOAD_PHOTO_URL: RegExp = /\/stores\/(\d+)\/photo\/(photos-stores|photos-inspections)\//;
const STORE_SET_PHOTOS_URL: RegExp = /\/stores\/(\d+)\/photos$/;

interface StoresForMapRequestBody {
    storeTaxNumbers?: string[];
}

export interface StoresListRequestBody {
    afterKey?: string;
    limit?: number;
    statuses?: string[];
    taxNumbers?: string[];
    companyTaxNumbers?: string[];
    names?: string[];
    taxationTypes?: string[];
    taxAuthorityIds?: number[];
    fromCashBoxesCount?: number;
    toCashBoxesCount?: number;
    inspectionStatuses?: string[];
    inspectionResults?: string[];
    regionCodes?: string[];
    mainActivities?: string[];
    middleActivities?: string[];
    lowerActivities?: string[];
}

class StoresFeatureMock extends BaseFeatureMock {
    // Lazily built: constructing 60k points is wasteful when a test never hits the map
    // endpoint. `onReset()` just nulls this out so the next access rebuilds it fresh.
    private storesForMapBackend: StoresForMapBackendStore | null = null;

    private get storesBackend(): StoresForMapBackendStore {
        if (!this.storesForMapBackend) {
            this.storesForMapBackend = new StoresForMapBackendStore({ total: 60000 });
        }
        return this.storesForMapBackend;
    }

    public readonly storesForMap = this.route<StoresForMap, StoresForMapRequestBody>({
        method: 'post',
        url: STORES_FOR_MAP_URL,
        defaultResponse: (config) => {
            const params = JSON.parse(config.data);
            const storesPoints = this.storesBackend.getStores();

            let data: StoresForMap = {
                clusters: storesClusters,
                points: storesPoints,
            };

            if (params.storeTaxNumbers?.length) {
                const matchedStore = data.points.find((store) => params.storeTaxNumbers.includes(store.taxNumber));

                data = {
                    clusters: [],
                    points: matchedStore ? [matchedStore] : [this.storesBackend.getStores()[0]],
                };
            }

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly storeDetailsForMap = this.route<StoreDetailsForMap>({
        method: 'get',
        url: STORES_DETAILS_FOR_MAP_URL,
        defaultResponse: () => {
            const data: StoreDetailsForMap = storeDetailsForMapFactory.item();

            return [200, data];
        },
        delayMs: () => Math.random() * 200,
    });

    public readonly listStores = this.route<StoresListDto, StoresListRequestBody>({
        method: 'post',
        url: STORES_URL,
        defaultResponse: (config) => {
            const body = JSON.parse(config.data);
            const data: StoresListDto = {
                afterKey: '234318932',
                stores: storeListItemDtoFactory.list({ count: body.limit }),
            };

            return [200, data];
        },
        delayMs: () => Math.random() * 200,
    });

    public readonly turnoversByCashboxes = this.route<RegistryTurnoversByCashBoxesDto>({
        method: 'get',
        url: STORES_GET_TURNOVERS_BY_CASHBOXES,
        defaultResponse: (config) => {
            const data: RegistryTurnoversByCashBoxesDto = {
                count: 100,
                turnovers: turnoversByCashBoxFactory.list({ count: config.params.limit }),
            };

            return [200, data];
        },
        delayMs: () => Math.random() * 1500,
    });

    public readonly turnoversByPeriod = this.route<RegistryTurnoversByPeriodDto[]>({
        method: 'get',
        url: STORES_GET_TURNOVERS_BY_PERIOD,
        defaultResponse: () => {
            const data: RegistryTurnoversByPeriodDto[] = TurnoversByPeriodFactory.list({ count: 12 });
            TurnoversByPeriodFactory.resetCount();

            return [200, data];
        },
        delayMs: () => Math.random() * 1500,
    });

    public readonly storeCashboxesStats = this.route<CashBoxesStats>({
        method: 'get',
        url: STORE_CASHBOXES_STATS,
        defaultResponse: () => {
            const data: CashBoxesStats = storeCashBoxesStatsFactory.item();

            return [200, data];
        },
        delayMs: () => Math.random() * 300,
    });

    public readonly storeCashboxes = this.route<StoreCashboxesListDto>({
        method: 'get',
        url: STORES_CASHBOXES_URL,
        defaultResponse: (config) => {
            const data: StoreCashboxesListDto = {
                count: 18,
                cashboxes: storeCashBoxesFactory.list({ count: config.params.limit }),
            };

            return [200, data];
        },
        delayMs: () => Math.random() * 300,
    });

    public readonly storeDetails = this.route<StoreDetailsDto>({
        method: 'get',
        url: STORES_DETAILS_URL,
        defaultResponse: () => {
            const data: StoreDetailsDto = storeDetailsFactory.item();

            return [200, data];
        },
        delayMs: () => Math.random() * 200,
    });

    public readonly updateStoreInfo = this.route<Partial<UpdatedStoreDetailsDto>>({
        method: 'patch',
        url: STORES_DETAILS_URL,
        defaultResponse: (config) => {
            const data: Partial<UpdatedStoreDetailsDto> = {};

            const body = JSON.parse(config.data);
            data.lat = body.lat;
            data.lng = body.lng;

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly coordinatesHistory = this.route<StoreCoordinatesHistoryDto>({
        method: 'get',
        url: COORDINATES_HISTORY,
        defaultResponse: () => {
            const data: StoreCoordinatesHistoryDto = {
                count: 5,
                items: coordinatesHistoryItemFactory.list({ count: 5 }),
            };

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly createInspection = this.route<StoreInspectionDto>({
        method: 'post',
        url: INSPECTIONS,
        defaultResponse: (config) => {
            const body = JSON.parse(config.data);
            const data: StoreInspectionDto = inspectionsFactory.item();
            data.status = InspectionStatus.OPEN;
            data.initiatorComment = body.initiatorComment;
            data.insertedAtUtc = moment().utc().format();

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly deleteInspection = this.route<{}>({
        method: 'delete',
        url: INSPECTIONS,
        defaultResponse: () => {
            return [200, {}];
        },
        delayMs: 300,
    });

    public readonly listInspections = this.route<StoreInspectionsHistoryDto>({
        method: 'get',
        url: INSPECTIONS,
        defaultResponse: () => {
            const data: StoreInspectionsHistoryDto = {
                count: 5,
                items: inspectionsFactory.list({ count: 5 }),
            };

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly updateInspection = this.route<StoreInspectionDto>({
        method: 'patch',
        url: INSPECTIONS,
        defaultResponse: (config) => {
            const body = JSON.parse(config.data);
            const data: StoreInspectionDto = inspectionsFactory.item();

            data.inspectionId = config.url.split('/')[4];
            data.status = body.status;

            if (body.status === InspectionStatus.CLOSED) {
                data.productName = body.productName;
                data.result = body.result;
                data.resultComment = body.resultComment;
                data.inspectedAtUtc = body.inspectedAtUtc;
            }

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly storePhotos = this.route<StorePhotoInfoDto[]>({
        method: 'get',
        url: STORE_PHOTOS_URL,
        defaultResponse: () => {
            const data: StorePhotoInfoDto[] = storePhotoInfoFactory.list({ count: 3 });

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly uploadStorePhoto = this.route<StorePhotoInfoDto[]>({
        method: 'post',
        url: STORE_UPLOAD_PHOTO_URL,
        defaultResponse: () => {
            const data: StorePhotoInfoDto[] = storePhotoInfoFactory.list({ count: 1 });

            return [200, data];
        },
        delayMs: 300,
    });

    public readonly setStorePhotos = this.route<{}>({
        method: 'patch',
        url: STORE_SET_PHOTOS_URL,
        defaultResponse: () => [200, {}],
        delayMs: 300,
    });

    public readonly deleteStorePhoto = this.route<{}>({
        method: 'delete',
        url: STORE_SET_PHOTOS_URL,
        defaultResponse: () => [200, {}],
        delayMs: 300,
    });

    protected onReset(): void {
        storeDetailsFactory.resetCount();
        storeListItemDtoFactory.resetCount();
        storeCashBoxesFactory.resetCount();
        storeCashBoxesStatsFactory.resetCount();
        turnoversByCashBoxFactory.resetCount();
        storeDetailsForMapFactory.resetCount();
        storeOnMapFactory.resetCount();
        coordinatesHistoryItemFactory.resetCount();
        inspectionsFactory.resetCount();
        storePhotoInfoFactory.resetCount();
        this.storesForMapBackend = null;
    }
}

export const storesMock = new StoresFeatureMock();

/** Backward-compat shim - dev mock mode (mockAdapter.ts) keeps calling this untouched. */
export function addStoreMockApi(mockAdapter: MockAdapter): void {
    storesMock.register(mockAdapter, { delay: true });
}

export class StoresForMapBackendStore {
    private stores: StorePoint[] = [];

    constructor(options: { total: number }) {
        for (let index = 0; index < options.total; index++) {
            const southWest = { lat: 40.384212768155045, lng: 49.83140945434571 };
            const northEast = { lat: 40.43427422075976, lng: 49.902734756469734 };

            const lngSpan = northEast.lng - southWest.lng;
            const latSpan = northEast.lat - southWest.lat;

            this.createStore(southWest.lat + latSpan * Math.random(), southWest.lng + lngSpan * Math.random());
        }
    }

    public getStores() {
        return this.stores;
    }

    private createStore(lat: number, lng: number): void {
        const store = storeOnMapFactory.item();

        store.lat = lat;
        store.lon = lng;

        this.stores.push(store);
    }
}

const storesClusters = [
    {
        count: 100,
        geoHash: 'tp5myn',
    },
    {
        count: 140,
        geoHash: 'tp5myx',
    },
    {
        count: 120,
        geoHash: 'tp5myg',
    },
    {
        count: 99,
        geoHash: 'tp6myx',
    },
    {
        count: 160,
        geoHash: 'tp9myx',
    },
];
